/**
 * useViewMode Hook
 * 
 * Simple hook for persisting view mode in localStorage.
 * 
 * @example
 * const [viewMode, setViewMode] = useViewMode('tickets', 'status');
 */

import { useState, useCallback, useEffect } from 'react';
import { ViewMode } from '../types/list.types';

// =============================================================================
// TYPES
// =============================================================================

export type UseViewModeReturn = [ViewMode, (mode: ViewMode) => void];

// =============================================================================
// HELPERS
// =============================================================================

const getStoredViewMode = (key: string, defaultMode: ViewMode): ViewMode => {
  try {
    const stored = localStorage.getItem(`${key}-view-mode`);
    if (stored === 'status' || stored === 'grid' || stored === 'table') {
      return stored;
    }
  } catch {
    // Ignore localStorage errors (SSR, private mode, etc.)
  }
  return defaultMode;
};

const setStoredViewMode = (key: string, mode: ViewMode) => {
  try {
    localStorage.setItem(`${key}-view-mode`, mode);
  } catch {
    // Ignore localStorage errors
  }
};

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for managing view mode with localStorage persistence.
 * 
 * @param storageKey Key to use in localStorage
 * @param defaultMode Default view mode (default: 'status')
 * @returns Tuple of [viewMode, setViewMode]
 */
export function useViewMode(
  storageKey: string,
  defaultMode: ViewMode = 'status'
): UseViewModeReturn {
  const [viewMode, setViewModeState] = useState<ViewMode>(() =>
    getStoredViewMode(storageKey, defaultMode)
  );

  // Persist view mode changes
  useEffect(() => {
    setStoredViewMode(storageKey, viewMode);
  }, [storageKey, viewMode]);

  // Safe setter
  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
  }, []);

  return [viewMode, setViewMode];
}

export default useViewMode;
